/**
 * Facebook and TikTok Pixel Loader
 * GDPR-compliant: Only loads when user consents to marketing cookies
 */

const FB_PIXEL_ID = process.env.REACT_APP_FACEBOOK_PIXEL_ID;
const TIKTOK_PIXEL_ID = process.env.REACT_APP_TIKTOK_PIXEL_ID;

let fbPixelLoaded = false;
let tiktokPixelLoaded = false;

/**
 * Initialize Facebook Pixel
 */
function initFacebookPixel() {
  if (fbPixelLoaded || !FB_PIXEL_ID) {
    if (!FB_PIXEL_ID) {
      console.log("[Pixels] Facebook Pixel ID not configured");
    }
    return;
  }

  // Facebook Pixel base code
  (function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(
    window,
    document,
    "script",
    "https://connect.facebook.net/en_US/fbevents.js"
  );

  window.fbq("init", FB_PIXEL_ID);
  window.fbq("track", "PageView");

  fbPixelLoaded = true;
  console.log("[Pixels] Facebook Pixel initialized:", FB_PIXEL_ID);
}

/**
 * Initialize TikTok Pixel (latest SDK with consent methods)
 */
function initTikTokPixel() {
  if (tiktokPixelLoaded || !TIKTOK_PIXEL_ID) {
    if (!TIKTOK_PIXEL_ID) {
      console.log("[Pixels] TikTok Pixel ID not configured");
    }
    return;
  }

  // TikTok Pixel base code (latest version with consent support)
  (function (w, d, t) {
    w.TiktokAnalyticsObject = t;
    var ttq = (w[t] = w[t] || []);
    ttq.methods = [
      "page",
      "track",
      "identify",
      "instances",
      "debug",
      "on",
      "off",
      "once",
      "ready",
      "alias",
      "group",
      "enableCookie",
      "disableCookie",
      "holdConsent",
      "revokeConsent",
      "grantConsent",
    ];
    ttq.setAndDefer = function (t, e) {
      t[e] = function () {
        t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
      };
    };
    for (var i = 0; i < ttq.methods.length; i++)
      ttq.setAndDefer(ttq, ttq.methods[i]);
    ttq.instance = function (t) {
      for (var e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++)
        ttq.setAndDefer(e, ttq.methods[n]);
      return e;
    };
    ttq.load = function (e, n) {
      var r = "https://analytics.tiktok.com/i18n/pixel/events.js";
      // eslint-disable-next-line no-unused-vars
      var o = n && n.partner;
      ttq._i = ttq._i || {};
      ttq._i[e] = [];
      ttq._i[e]._u = r;
      ttq._t = ttq._t || {};
      ttq._t[e] = +new Date();
      ttq._o = ttq._o || {};
      ttq._o[e] = n || {};
      var s = document.createElement("script");
      s.type = "text/javascript";
      s.async = true;
      s.src = r + "?sdkid=" + e + "&lib=" + t;
      var a = document.getElementsByTagName("script")[0];
      a.parentNode.insertBefore(s, a);
    };

    ttq.load(TIKTOK_PIXEL_ID);
    ttq.page();
  })(window, document, "ttq");

  tiktokPixelLoaded = true;
  console.log("[Pixels] TikTok Pixel initialized:", TIKTOK_PIXEL_ID);
}

/**
 * Load all marketing pixels (call when user consents to marketing cookies)
 */
export function loadMarketingPixels() {
  initFacebookPixel();
  initTikTokPixel();
}

/**
 * Check consent and load pixels if already consented
 * Call this on app initialization
 */
export function initPixelsFromConsent() {
  const consentStr = localStorage.getItem("cookie_consent");
  if (!consentStr) return;

  try {
    const consent = JSON.parse(consentStr);
    if (consent.marketing) {
      loadMarketingPixels();
    }
  } catch (e) {
    console.error("[Pixels] Error reading consent:", e);
  }
}

/**
 * Track Facebook custom event with optional user data for Advanced Matching
 */
export function trackFacebookEvent(eventName, params = {}, userData = null) {
  if (window.fbq && fbPixelLoaded) {
    if (userData) {
      // Send user data for Advanced Matching (Facebook auto-hashes these)
      window.fbq("track", eventName, params, {
        userData: {
          em: userData.email || undefined,      // email
          ph: userData.phone || undefined,      // phone
          fn: userData.firstName || undefined,  // first name
          ln: userData.lastName || undefined,   // last name
        },
      });
    } else {
      window.fbq("track", eventName, params);
    }
  }
}

/**
 * Track TikTok custom event with optional user data
 */
export function trackTikTokEvent(eventName, params = {}, userData = null) {
  if (window.ttq && tiktokPixelLoaded) {
    if (userData) {
      // TikTok Advanced Matching - identify user first
      window.ttq.identify({
        email: userData.email || undefined,
        phone_number: userData.phone || undefined,
      });
    }
    window.ttq.track(eventName, params);
  }
}

/**
 * Track event on both platforms with optional user data
 * @param {string} eventName - Event name (e.g., "Lead", "Purchase")
 * @param {object} params - Event parameters
 * @param {object} userData - User data for advanced matching { email, phone, firstName, lastName }
 */
export function trackEvent(eventName, params = {}, userData = null) {
  trackFacebookEvent(eventName, params, userData);
  trackTikTokEvent(eventName, params, userData);
}
